import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, CreditCard, Loader2, Send, UserCog } from 'lucide-react';
import { Button } from '../ui/button';
import { useT } from '../../services/i18n';

const CheckoutPaymentStep = ({ assist, paymentMethod, setPaymentMethod, toPay, total, formError, orderId, orderComplete, retryPayment, isProcessing, redirecting, shortages, onBack, onSubmit }) => {
  const { t } = useT();
  const methods = [{ id: 'card', label: t('checkout.card'), icons: ['Visa', 'Mastercard', 'Apple Pay', 'Google Pay'] }];
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <CreditCard className="text-orange-500" size={24} />
        {t('checkout.paymentMethod')}
      </h2>

      {assist && (
        <div className="mb-5 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 flex gap-3" data-testid="checkout-assist-notice">
          <UserCog size={18} className="shrink-0 mt-0.5" />
          <div><p className="font-semibold">Commande par téléphone (mode assistance)</p><p className="text-xs mt-1">Aucun paiement n'est saisi ici : la commande sera créée « En attente de paiement » et le client recevra par e-mail un lien sécurisé (valable 7 jours) pour régler lui-même. Les articles sont réservés dès maintenant.</p></div>
        </div>
      )}

      <div className={`space-y-3 ${assist ? 'opacity-50 pointer-events-none' : ''}`}>
        {methods.map(method => (
          <label key={method.id} className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-colors ${paymentMethod === method.id ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
            <div className="flex items-center gap-3">
              <input data-testid={`payment-method-${method.id}`} type="radio" name="paymentMethod" value={method.id} checked={paymentMethod === method.id} onChange={(e) => setPaymentMethod(e.target.value)} className="w-4 h-4 text-orange-500" />
              <span className="font-medium text-gray-900">{method.label}</span>
            </div>
            <div className="flex gap-2">
              {method.icons.map(icon => <span key={icon} className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600">{icon}</span>)}
            </div>
          </label>
        ))}
      </div>

      {paymentMethod === 'card' && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg" data-testid="checkout-sumup-info">
          <p className="text-sm text-gray-600 mb-4">{toPay > 0 ? t('checkout.sumupInfo') : t('checkout.walletCoversAll')}</p>
          <div className="flex items-center gap-2 text-sm text-green-600"><Shield size={16} />{t('checkout.ssl')} · SumUp</div>
        </div>
      )}

      {formError && (
        <div className="mt-4" data-testid="checkout-error-block">
          <p data-testid="checkout-error" className="text-sm text-red-600 font-medium">{formError}</p>
          {orderId && !orderComplete && (
            <Button data-testid="checkout-retry-payment" onClick={retryPayment} disabled={isProcessing} className="mt-3 bg-orange-500 hover:bg-orange-600">{t('checkout.retryPayment')}</Button>
          )}
          {shortages.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 space-y-1" data-testid="checkout-shortages">
              {shortages.map(s => (
                <li key={s.product_id} data-testid={`checkout-shortage-${s.product_id}`}>
                  <strong>{s.name}</strong> {s.brand && <span className="text-red-500">({s.brand})</span>} : {t('checkout.requested', { n: s.requested, count: s.requested })}, {s.available === 0 ? t('common.outOfStock').toLowerCase() : t('checkout.available', { n: s.available, count: s.available })}
                </li>
              ))}
              <li className="pt-1"><Link to="/panier" className="text-blue-600 hover:underline" data-testid="checkout-fix-cart">{t('checkout.fixCart')}</Link></li>
            </ul>
          )}
        </div>
      )}

      <div className="mt-6 flex justify-between">
        <Button data-testid="checkout-back-button" variant="outline" onClick={onBack}>{t('common.back')}</Button>
        <Button data-testid="checkout-pay-button" onClick={onSubmit} disabled={isProcessing || (!!orderId && !orderComplete)} className={assist ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-orange-500 hover:bg-orange-600'}>
          {isProcessing || redirecting ? (
            <><Loader2 size={16} className="mr-2 animate-spin" />{redirecting ? t('checkout.redirecting') : t('checkout.processing')}</>
          ) : assist ? (
            <><Send size={16} className="mr-2" /> Créer la commande et envoyer le lien de paiement ({total.toFixed(2)} €)</>
          ) : (
            t('checkout.pay', { amount: toPay.toFixed(2) })
          )}
        </Button>
      </div>
    </div>
  );
};

export default CheckoutPaymentStep;
